/**
 * Model Config tab — Model assignments with effective date/time (Angular property-model-config).
 * Angular-style: each row’s model / effective dates are read-only until its pencil
 * is clicked (then save again with the pencil). Delete is available whenever the
 * user may remove the row—no pencil step required. Header Save still flushes dirty
 * rows via PropertyEdit.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Calendar, ChevronDown, Clock, Loader2, Layers, Pencil, Plus, Trash2, X } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { companyEditConfirmPresentation } from '@/features/company/constants'
import { SectionCard } from '@/components/ui/SectionCard'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import configTableStyles from '@/features/property/styles/propertyConfigTable.module.css'
import { DatePicker } from '@/components/ui/DatePicker'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import {
  CANNOT_DELETE_MODEL_MAPPING_MESSAGE,
  buildSelectedModelsJson,
  deletePropertyModelRow,
  extractPropertyModels,
  fetchActiveModelsList,
  fetchCanDeleteModelMapping,
  fetchCanEditModelMapping,
} from '@/features/property/services/propertyModelService'
import {
  cascadeEffectiveToForNextRow,
  determineConfigDateCase,
  getTomorrowEasternDate,
  isConfigDateRangeActive,
  isConfigDateStringOnOrBeforeEasternToday,
  isConfigSelectLockedForActiveCase1,
  isEffectiveFromCalendarDayAllowed,
  makeDefaultBaseModelRow,
  normalizeConfigDate,
  type ConfigEditMode,
} from '@/features/property/utils/propertyConfigEditLogic'
import type {
  ModelRowFieldErrors,
  PropertyModelConfigTabHandle,
  PropertyModelConfigTabProps,
} from '../types'

/* ────────────── helpers ────────────── */

/** Angular: Base is strictly model_id === 1. */
function isBaseModelRow(r: Record<string, unknown>): boolean {
  return Number(r.model_id) === 1
}

function rowId(r: Record<string, unknown>): string | number | undefined {
  return (r.property_model_map_id ?? r.id ?? r.property_model_id ?? r.pp_id) as
    | string
    | number
    | undefined
}

/** Prefer server map id for DELETE; avoid accidental use of unrelated `id` shapes. */
function propertyModelMapIdForDelete(r: Record<string, unknown>): string | number | undefined {
  const m = r.property_model_map_id
  if (m != null && String(m).trim() !== '' && !String(m).startsWith('new-')) {
    return m as string | number
  }
  const id = r.id
  if (typeof id === 'number' && Number.isFinite(id)) return id
  if (typeof id === 'string' && /^\d+$/.test(id.trim())) return Number(id.trim())
  return undefined
}

function toInputDate(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function toDisplayDate(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

function toDisplayTime(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${String(h).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')} ${ampm}`
}

function toInputTime(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

function combineDateTimeLocal(date: string, time: string): string | null {
  if (!date) return null
  const t = time && time.length >= 5 ? time : '00:00:00'
  const full = t.length === 5 ? `${t}:00` : t
  const d = new Date(`${date}T${full}`)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const dy = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${mo}-${dy} ${hh}:${mm}:${ss}`
}

function isNewDraftRow(r: Record<string, unknown>): boolean {
  return String(rowId(r) ?? '').startsWith('new-')
}

function computeModelRowFieldErrors(
  r: Record<string, unknown>,
  index: number,
  allRows: Record<string, unknown>[],
): ModelRowFieldErrors {
  if (isBaseModelRow(r)) return {}
  const errors: ModelRowFieldErrors = {}
  const mid = Number(r.model_id ?? 0)
  if (!mid) errors.model = 'Model is required'

  const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
  const fromLocal = combineDateTimeLocal(
    fromD,
    String(r._effective_from_time ?? (toInputTime(r.effective_from as string) || '00:00:00')),
  )

  if (!fromD) {
    errors.effectiveFrom = 'Effective From is required.'
  } else if (isNewDraftRow(r) && fromD) {
    if (isConfigDateStringOnOrBeforeEasternToday(fromD)) {
      errors.effectiveFrom = 'Effective From must be a future date from today.'
    }
  }

  const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
  const toLocal = toD
    ? combineDateTimeLocal(
        toD,
        String(r._effective_to_time ?? (toInputTime(r.effective_to as string) || '00:00:00')),
      )
    : null

  const isFirstNonBase = !allRows.slice(0, index).some((row) => !isBaseModelRow(row))
  if (!isFirstNonBase && !toD) {
    errors.effectiveTo = 'Effective To is required.'
  }

  if (isNewDraftRow(r) && toD && !fromD) {
    if (isConfigDateStringOnOrBeforeEasternToday(toD)) {
      errors.effectiveTo = 'Effective To must be a future date from today.'
    }
  }

  if (fromLocal && toLocal) {
    const fromMs = new Date(fromLocal.replace(' ', 'T')).getTime()
    const toMs = new Date(toLocal.replace(' ', 'T')).getTime()
    if (toMs <= fromMs) {
      errors.effectiveTo =
        'Effective To must be after Effective From. When both dates are the same, the time must be different.'
    } else {
      const key = rowKey(r, index)
      const overlap = checkDateTimeOverlap(allRows, key, fromLocal, toLocal)
      if (overlap.overlap) {
        errors.overlap = 'Date range overlaps with another model.'
      }
    }
  }

  return errors
}

function ConfigFieldError({ message, testId }: { message?: string; testId?: string }) {
  if (!message) return null
  return (
    <div
      className="mt-1 text-sm leading-snug text-red-600"
      role="alert"
      data-testid={testId}
    >
      {message}
    </div>
  )
}

function isModelRowActive(r: Record<string, unknown>, _allRows: Record<string, unknown>[]): boolean {
  if (isBaseModelRow(r)) {
    return !_allRows.some((other) => {
      if (isBaseModelRow(other)) return false
      const fromD = String(other._effective_from_date ?? toInputDate(other.effective_from as string))
      const fromLocal = combineDateTimeLocal(
        fromD,
        String(other._effective_from_time ?? (toInputTime(other.effective_from as string) || '00:00:00')),
      )
      const toD = String(other._effective_to_date ?? toInputDate(other.effective_to as string))
      const toLocal = toD
        ? combineDateTimeLocal(
            toD,
            String(other._effective_to_time ?? (toInputTime(other.effective_to as string) || '23:59:59')),
          )
        : null
      return isConfigDateRangeActive(fromLocal, toLocal)
    })
  }
  const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
  const fromLocal = combineDateTimeLocal(
    fromD,
    String(r._effective_from_time ?? (toInputTime(r.effective_from as string) || '00:00:00')),
  )
  const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
  const toLocal = toD
    ? combineDateTimeLocal(
        toD,
        String(r._effective_to_time ?? (toInputTime(r.effective_to as string) || '23:59:59')),
      )
    : null
  return isConfigDateRangeActive(fromLocal, toLocal)
}

function sortProductRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows.filter((r) => !isBaseModelRow(r)), ...rows.filter((r) => isBaseModelRow(r))]
}

function parseDateStrToDate(s: string): Date | null {
  if (!s) return null
  const parts = s.split('-').map(Number)
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function normalizeDateObj(date: Date, isEndOfDay = false): Date {
  return normalizeConfigDate(date, isEndOfDay)
}

/** Angular datesOverlap — inclusive endpoints (touching From/To counts as overlap). */
function datesOverlap(from1: Date | null, to1: Date | null, from2: Date | null, to2: Date | null): boolean {
  if ((!from1 && !to1) || !from2 || !to2) return false
  const f1 = from1 ?? new Date(0)
  const t1 = to1 ?? new Date(9999999999999)
  return (f1 >= from2 && f1 <= to2) || (t1 >= from2 && t1 <= to2) || (f1 <= from2 && t1 >= to2)
}

/**
 * Angular isDateBlockedByOtherModels: a calendar day is blocked only when another non-base
 * model's CLOSED range fully covers that day (from <= 00:00:00 and to >= 23:59:59).
 * Open-ended ranges (blank Effective To) and the current row never block.
 *
 * Compare against 23:59:59.000 (not .999) so cascaded / API times of `23:59:59` still cover the day.
 */
function isDayBlockedByOtherModels(
  day: Date,
  currentKey: string,
  rows: Record<string, unknown>[],
): boolean {
  const startOfDay = normalizeDateObj(day)
  const endOfDayFloor = normalizeDateObj(day)
  endOfDayFloor.setHours(23, 59, 59, 0)
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (rowKey(row, i) === currentKey) continue
    if (isBaseModelRow(row)) continue

    const fromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
    const toD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
    if (!fromD || !toD) continue

    const fromLocal = combineDateTimeLocal(
      fromD,
      String(row._effective_from_time ?? (toInputTime(row.effective_from as string) || '00:00:00')),
    )
    const toLocal = combineDateTimeLocal(
      toD,
      String(row._effective_to_time ?? (toInputTime(row.effective_to as string) || '00:00:00')),
    )
    if (!fromLocal || !toLocal) continue

    const from = new Date(fromLocal.replace(' ', 'T'))
    const to = new Date(toLocal.replace(' ', 'T'))
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) continue

    if (from.getTime() <= startOfDay.getTime() && to.getTime() >= endOfDayFloor.getTime()) return true
  }
  return false
}

function checkDateTimeOverlap(
  rows: Record<string, unknown>[],
  currentKey: string,
  effectiveFrom: string,
  effectiveTo: string | null
): { overlap: boolean; productName?: string } {
  const fromA = new Date(effectiveFrom.replace(' ', 'T'))
  if (Number.isNaN(fromA.getTime())) return { overlap: false }
  // Angular conditionalModelDateValidator: overlap only when current row has BOTH dates.
  if (!effectiveTo) return { overlap: false }
  const toA = new Date(effectiveTo.replace(' ', 'T'))
  if (Number.isNaN(toA.getTime())) return { overlap: false }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (rowKey(row, i) === currentKey) continue
    if (isBaseModelRow(row)) continue

    const oFromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
    const oFromT = String(row._effective_from_time ?? toInputTime(row.effective_from as string))
    const oToD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
    const oToT = String(row._effective_to_time ?? toInputTime(row.effective_to as string))

    const oFromStr = combineDateTimeLocal(oFromD, oFromT)
    if (!oFromStr) continue
    const oToStr = oToD ? combineDateTimeLocal(oToD, oToT) : null

    const fromB = new Date(oFromStr.replace(' ', 'T'))
    if (Number.isNaN(fromB.getTime())) continue
    // Angular hasModelDateOverlap: blank other Effective To = unbounded far-future range.
    const toB = oToStr ? new Date(oToStr.replace(' ', 'T')) : new Date(9999999999999)
    if (oToStr && Number.isNaN(toB.getTime())) continue

    if (datesOverlap(fromA, toA, fromB, toB)) {
      return { overlap: true, productName: displayProductName(row) }
    }
  }
  return { overlap: false }
}

function displayProductName(r: Record<string, unknown>): string {
  return String(r.model_name ?? r.name ?? r.title ?? r.model_code ?? '')
}

function catalogOptionLabel(r: Record<string, unknown>): string {
  return String(r.model_name ?? r.name ?? r.title ?? 'Model')
}

function catalogOptionValue(r: Record<string, unknown>): string {
  return String(r.model_id ?? r.id ?? '')
}

/** Angular getBaseModelName — catalog model_id === 1, else 'SM 04.26'. */
function getBaseModelName(catalog: Record<string, unknown>[]): string {
  const base = catalog.find((c) => Number(catalogOptionValue(c)) === 1)
  return base ? catalogOptionLabel(base) : 'SM 04.26'
}

function rowKey(r: Record<string, unknown>, i: number): string {
  return rowId(r) != null ? String(rowId(r)) : `idx-${i}`
}

/** Server snapshot row that matches this UI row key (used when leaving a row without saving). */
function findServerRowForUiKey(
  key: string,
  property: Record<string, unknown> | null
): Record<string, unknown> | undefined {
  const list = sortProductRows(extractPropertyModels(property))
  return list.find((row, i) => rowKey(row, i) === key)
}

function parseApiDateTime(s: unknown): number | null {
  if (s == null || s === '') return null
  const t = new Date(String(s)).getTime()
  return Number.isNaN(t) ? null : t
}

/** True when non-base rows (including new drafts) differ from `property.current_models`. */
function rowsDifferFromPropertySnapshot(
  localRows: Record<string, unknown>[],
  property: Record<string, unknown> | null
): boolean {
  const serverRows = sortProductRows(extractPropertyModels(property))
  const localNB = localRows.filter((r) => !isBaseModelRow(r))
  const serverNB = serverRows.filter((r) => !isBaseModelRow(r))
  const serverByMapId = new Map<string, Record<string, unknown>>()
  for (const s of serverNB) {
    const id = propertyModelMapIdForDelete(s) ?? rowId(s)
    if (id != null) serverByMapId.set(String(id), s)
  }

  for (const r of localNB) {
    const rid = rowId(r)
    if (rid != null && String(rid).startsWith('new-')) return true

    const mapId = propertyModelMapIdForDelete(r) ?? rid
    if (mapId == null) return true
    const s = serverByMapId.get(String(mapId))
    if (!s) return true

    if (String(r.model_id ?? '') !== String(s.model_id ?? '')) return true

    const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
    const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
    const localFromMs = parseApiDateTime(combineDateTimeLocal(fromD, fromT))
    const serverFromMs = parseApiDateTime(s.effective_from)
    if (localFromMs !== serverFromMs) return true

    const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
    const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
    const localTo = toD ? combineDateTimeLocal(toD, toT) : null
    const localToMs = localTo ? parseApiDateTime(localTo) : null
    const serverToMs = parseApiDateTime(s.effective_to)
    if (localToMs !== serverToMs) return true
  }

  return localNB.length !== serverNB.length
}

/** Same visual weight as DatePicker calendar icon (h-5). */
const DATE_FIELD_ICON = 'pointer-events-none absolute right-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400'

/** Matches native time input / browser clock scale (smaller than calendar). */
const TIME_FIELD_CLOCK_CLASS =
  'pointer-events-none absolute right-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray-400'

const TIME_INPUT_CLASS =
  'box-border h-[42px] w-full rounded-md border border-slate-200 bg-white py-2.5 pl-4 pr-9 text-sm leading-5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)] disabled:cursor-not-allowed disabled:bg-white disabled:text-slate-900 disabled:opacity-100 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-9 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0'

/** Angular date-wrapper: date flex-fills column, time fixed, gap ~6px. */
const CONFIG_CELL_PAD = 'align-top'

function TimeFieldInput({
  value,
  onChange,
  disabled,
  dataTestId,
  className,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  dataTestId?: string
  className?: string
}) {
  return (
    <div className={cn(className)}>
      <input type="time" step={1} className={TIME_INPUT_CLASS} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} data-testid={dataTestId} />
      <Clock strokeWidth={1.5} className={TIME_FIELD_CLOCK_CLASS} aria-hidden />
    </div>
  )
}

/** Matches DatePicker + time input: same heights; fills Effective From/To column like Angular. */
function ReadOnlyEffectiveFields({
  dateLabel,
  timeLabel,
  muted,
  dateTimeRowClass,
  dateFieldClass,
  timeFieldClass,
}: {
  dateLabel: string
  timeLabel: string
  muted?: boolean
  dateTimeRowClass: string
  dateFieldClass: string
  timeFieldClass: string
}) {
  const textCls = muted ? 'text-slate-400' : 'text-slate-800'
  const dateBox =
    'box-border h-[42px] w-full rounded-md border border-slate-200 bg-white py-2.5 pl-3 pr-10 text-sm leading-5 flex items-center'
  const timeBox =
    'box-border h-[42px] w-full rounded-md border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm leading-5 flex items-center'
  return (
    <div className={dateTimeRowClass}>
      <div className={cn('relative', dateFieldClass)}>
        <div className={cn(dateBox, textCls)}>{dateLabel || '—/—/——'}</div>
        <Calendar className={DATE_FIELD_ICON} aria-hidden />
      </div>
      <div className={timeFieldClass}>
        <div className={cn(timeBox, textCls)}>{timeLabel || '—:—:— —'}</div>
        <Clock strokeWidth={1.5} className={TIME_FIELD_CLOCK_CLASS} aria-hidden />
      </div>
    </div>
  )
}

/* ────────────── component ────────────── */

export const PropertyModelConfigTab = forwardRef<PropertyModelConfigTabHandle, PropertyModelConfigTabProps>(
  function PropertyModelConfigTab({ property, propertyId: _propertyId, onRefresh, onRequestSave, onDirty }, ref) {
    void _propertyId
    const { hasPolicy } = useUserRole()
    /** Angular `allow_model_config` — without it the Model Information UI is not shown / not saved. */
    const allowModelConfig = hasPolicy(['allow_model_config'])
    const canEdit = allowModelConfig && hasPolicy(['property_modelconfig_edit'])
    const canDelete = allowModelConfig && hasPolicy(['property_modelconfig_delete'])
    const canMutate = canEdit

    const [rows, setRows] = useState<Record<string, unknown>[]>([])
    const [catalog, setCatalog] = useState<Record<string, unknown>[]>([])
    const [loading, setLoading] = useState(true)
    const [savingId, setSavingId] = useState<string | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<{
      id: string | number
      label: string
      message: string
    } | null>(null)
    /** Map id while GET can_delete_model_mapping runs (Angular pre-delete check). */
    const [deletePrecheckMapId, setDeletePrecheckMapId] = useState<string | number | null>(null)
    /** UI row key (`rowKey`) currently in inline-edit mode; only that row’s fields are enabled. */
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
    /** Angular enableModelEditing: full vs CASE_1_400 To-only. */
    const [editModeByKey, setEditModeByKey] = useState<Record<string, ConfigEditMode>>({})
    /**
     * Angular `autoFilledEffectiveTo` / `enableBlankEffectiveTo` —
     * saved rows with blank Effective To unlocked for To-only editing after Add New.
     */
    const [effectiveToUnlockKeys, setEffectiveToUnlockKeys] = useState<Record<string, true>>({})
    /** Angular `validateApplicationForm` — show inline field errors after Add New / save attempt. */
    const [forceShowErrors, setForceShowErrors] = useState(false)

    const rowsRef = useRef(rows)
    rowsRef.current = rows
    const propertyRef = useRef(property)
    propertyRef.current = property

    const loadProducts = useCallback(() => {
      setLoading(true)
      try {
        const list = extractPropertyModels(property)
        setRows(list.length === 0 ? [makeDefaultBaseModelRow('SM 04.26')] : sortProductRows(list))
        setEditingRowKey(null)
        setEditModeByKey({})
        setEffectiveToUnlockKeys({})
        setForceShowErrors(false)
      } finally {
        setLoading(false)
      }
    }, [property])

    useEffect(() => {
      loadProducts()
    }, [loadProducts])

    useEffect(() => {
      let cancelled = false
      void fetchActiveModelsList()
        .then((list) => {
          if (cancelled) return
          setCatalog(list)
          const baseName = getBaseModelName(list)
          setRows((prev) => {
            if (
              prev.length === 1 &&
              Number(prev[0].model_id) === 1 &&
              propertyModelMapIdForDelete(prev[0]) == null
            ) {
              if (String(prev[0].model_name ?? '') !== baseName) {
                return [{ ...prev[0], model_name: baseName }]
              }
            }
            return prev
          })
        })
        .catch((err) => {
          if (!cancelled) {
            setCatalog([])
            showApiError(err)
          }
        })
      return () => {
        cancelled = true
      }
    }, [])

    const updateRow = (key: string, patch: Record<string, unknown>) => {
      const isUnlockedTo = Boolean(effectiveToUnlockKeys[key])
      const editMode = editModeByKey[key]
      const target = rowsRef.current.find((r, i) => rowKey(r, i) === key)
      const toOnlyKeys = ['_effective_to_date', '_effective_to_time', 'effective_to']
      const allow =
        (target && isNewDraftRow(target)) ||
        (editingRowKey === key && editMode !== 'to-only') ||
        ((isUnlockedTo || (editingRowKey === key && editMode === 'to-only')) &&
          Object.keys(patch).every((k) => toOnlyKeys.includes(k)))
      if (!allow) return

      setRows((prev) => {
        let next = prev.map((r, i) => {
          const k = rowKey(r, i)
          return k === key ? { ...r, ...patch } : r
        })

        if ('_effective_from_date' in patch || '_effective_from_time' in patch || 'effective_from' in patch) {
          const idx = next.findIndex((r, i) => rowKey(r, i) === key)
          if (idx >= 0) {
            const cur = next[idx]
            const fromD = String(cur._effective_from_date ?? toInputDate(cur.effective_from as string))
            const fromT = String(
              cur._effective_from_time ?? (toInputTime(cur.effective_from as string) || '00:00:00'),
            )
            const fromLocal = combineDateTimeLocal(fromD, fromT)
            if (fromLocal && idx + 1 < next.length) {
              const below = next[idx + 1]
              if (!isBaseModelRow(below)) {
                const belowTo = String(below._effective_to_date ?? toInputDate(below.effective_to as string))
                if (!belowTo) {
                  const belowFromD = String(below._effective_from_date ?? toInputDate(below.effective_from as string))
                  const belowFromT = String(
                    below._effective_from_time ?? (toInputTime(below.effective_from as string) || '00:00:00'),
                  )
                  const belowFromLocal = belowFromD ? combineDateTimeLocal(belowFromD, belowFromT) : null
                  const autoTo = cascadeEffectiveToForNextRow(fromLocal, belowFromLocal)
                  if (autoTo) {
                    const belowKey = rowKey(below, idx + 1)
                    next = next.map((r, i) =>
                      rowKey(r, i) === belowKey
                        ? {
                            ...r,
                            effective_to: autoTo,
                            _effective_to_date: toInputDate(autoTo),
                            _effective_to_time: toInputTime(autoTo),
                          }
                        : r,
                    )
                    setEffectiveToUnlockKeys((prevUnlock) => ({ ...prevUnlock, [belowKey]: true }))
                  }
                }
              }
            }
          }
        }
        return next
      })
      onDirty?.()
    }

    const revertRowToServerSnapshot = useCallback((key: string) => {
      const serverRow = findServerRowForUiKey(key, propertyRef.current)
      setRows((prevRows) => {
        if (!serverRow) {
          return prevRows.filter((r, i) => rowKey(r, i) !== key)
        }
        return prevRows.map((r, i) => (rowKey(r, i) === key ? { ...serverRow } : r))
      })
    }, [])

    /** Angular addModelRow — cancel edits, validate top draft, insert at top, unlock blank Effective To. */
    const handleAddRow = () => {
      if (!canMutate) return

      const snapshot = rowsRef.current

      if (editingRowKey != null) {
        const editing = snapshot.find((r, i) => rowKey(r, i) === editingRowKey)
        if (editing && !isNewDraftRow(editing)) {
          revertRowToServerSnapshot(editingRowKey)
        }
      }
      setEditingRowKey(null)
      setEditModeByKey({})
      setEffectiveToUnlockKeys({})
      setForceShowErrors(true)

      const ordered = sortProductRows(snapshot)
      const top = ordered[0]
      if (top && !isBaseModelRow(top) && isNewDraftRow(top)) {
        const topKey = rowKey(top, 0)
        const topErrors = computeModelRowFieldErrors(top, 0, ordered)
        if (topErrors.model || topErrors.effectiveFrom || topErrors.effectiveTo || topErrors.overlap) {
          setEditingRowKey(topKey)
          return
        }
      }

      // Angular addModelRow: length === 0 → addDefaultBaseModel only
      if (snapshot.length === 0) {
        setRows([makeDefaultBaseModelRow(getBaseModelName(catalog))])
        setEditingRowKey(null)
        return
      }

      const tempId = `new-${Date.now()}`
      const newKey = String(tempId)
      const unlock: Record<string, true> = {}
      for (let i = 0; i < snapshot.length; i++) {
        const r = snapshot[i]
        if (isBaseModelRow(r) || isNewDraftRow(r)) continue
        if (propertyModelMapIdForDelete(r) == null) continue
        const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
        if (!toD) unlock[rowKey(r, i)] = true
      }

      setRows([
        {
          id: tempId,
          model_id: '',
          model_name: '',
          effective_from: null,
          effective_to: null,
        },
        ...snapshot.filter((r) => !isBaseModelRow(r)),
        ...snapshot.filter((r) => isBaseModelRow(r)),
      ])
      setEffectiveToUnlockKeys(unlock)
      setEditingRowKey(newKey)
      onDirty?.()
    }

    const upsertRowByKey = useCallback(
      async (key: string, rowSnapshot: Record<string, unknown>[]): Promise<boolean> => {
        const r = rowSnapshot.find((row, i) => rowKey(row, i) === key)
        if (!r || isBaseModelRow(r)) return true

        const pid = Number(r.model_id ?? 0)
        if (!pid) {
          showError('Model is required.')
          return false
        }

        const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
        const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
        const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
        const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
        const effective_from = combineDateTimeLocal(fromD, fromT)
        const effective_to = toD ? combineDateTimeLocal(toD, toT) : null
        if (!effective_from) {
          showError('Effective From date and time are required.')
          return false
        }

        const isNewDraft = String(rowId(r) ?? '').startsWith('new-')
        if (isNewDraft) {
          if (isConfigDateStringOnOrBeforeEasternToday(fromD)) {
            showError('Effective From must be a future date from today.')
            return false
          }
        }

        if (effective_to) {
          const fromMs = new Date(effective_from.replace(' ', 'T')).getTime()
          const toMs = new Date(effective_to.replace(' ', 'T')).getTime()
          if (toMs <= fromMs) {
            showError(
              'Effective To must be after Effective From. When both dates are the same, the time must be different.',
            )
            return false
          }
        }

        const overlapCheck = checkDateTimeOverlap(rowSnapshot, key, effective_from, effective_to)
        if (overlapCheck.overlap) {
          showError(
            `Date range overlaps with "${overlapCheck.productName || 'another model'}". No two models can have overlapping effective dates.`
          )
          return false
        }

        // Persist via selected_models on main property POST (Angular) — no /api/property/model.
        return true
      },
      []
    )

    const buildSelectedModelsFromRows = useCallback((snapshot: Record<string, unknown>[]) => {
      const items = snapshot
        .filter((r) => !isBaseModelRow(r))
        .map((r) => {
          const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
          const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
          const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
          const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
          const mapId = propertyModelMapIdForDelete(r)
          return {
            model_id: Number(r.model_id ?? 0),
            effective_from: combineDateTimeLocal(fromD, fromT),
            effective_to: toD ? combineDateTimeLocal(toD, toT) : null,
            property_model_map_id:
              mapId != null && !String(mapId).startsWith('new-') ? Number(mapId) : undefined,
          }
        })
      return buildSelectedModelsJson(items)
    }, [])

    const handleSaveRow = async (key: string) => {
      const r = rows.find((row, i) => rowKey(row, i) === key)
      if (!r || isBaseModelRow(r)) return

      setSavingId(key)
      let committed = false
      try {
        const ok = await upsertRowByKey(key, rowsRef.current)
        if (!ok) return
        setEditingRowKey(null)
        setEditModeByKey((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        committed = true
        // Angular persists models only on main property Save (selected_models).
        onRequestSave?.()
      } catch (err) {
        showApiError(err)
      } finally {
        setSavingId(null)
        if (committed) setEditingRowKey(null)
      }
    }

    const handlePencilClick = async (key: string) => {
      if (!canEdit || savingId != null) return
      if (editingRowKey === key) {
        void handleSaveRow(key)
        return
      }
      if (editingRowKey != null) {
        revertRowToServerSnapshot(editingRowKey)
        setEditModeByKey((prev) => {
          const next = { ...prev }
          delete next[editingRowKey]
          return next
        })
      }

      const row = rowsRef.current.find((r, i) => rowKey(r, i) === key)
      if (!row || isBaseModelRow(row)) return

      const fromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
      const fromT = String(row._effective_from_time ?? (toInputTime(row.effective_from as string) || '00:00:00'))
      const toD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
      const toT = String(row._effective_to_time ?? (toInputTime(row.effective_to as string) || '00:00:00'))
      const fromLocal = combineDateTimeLocal(fromD, fromT)
      const toLocal = toD ? combineDateTimeLocal(toD, toT) : null
      const caseType = determineConfigDateCase(fromLocal, toLocal)

      if (caseType === 'CASE_2') {
        setEditingRowKey(key)
        setEditModeByKey((prev) => ({ ...prev, [key]: 'full' }))
        return
      }

      const mapId = propertyModelMapIdForDelete(row)
      if (mapId == null) {
        setEditingRowKey(key)
        setEditModeByKey((prev) => ({ ...prev, [key]: 'full' }))
        return
      }

      setSavingId(key)
      try {
        const { allowed, message } = await fetchCanEditModelMapping(mapId)
        if (allowed) {
          setEditingRowKey(key)
          setEditModeByKey((prev) => ({ ...prev, [key]: 'full' }))
        } else if (caseType === 'CASE_1') {
          setEditingRowKey(key)
          setEditModeByKey((prev) => ({ ...prev, [key]: 'to-only' }))
        } else {
          showError(message ?? 'Cannot edit this model configuration.')
        }
      } catch (err) {
        showApiError(err)
      } finally {
        setSavingId(null)
      }
    }

    const requestDeleteRow = async (r: Record<string, unknown>, label: string) => {
      const mapId = propertyModelMapIdForDelete(r)
      if (mapId == null) {
        showError('Cannot remove this row: missing model map id.')
        return
      }

      const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
      const fromT = String(r._effective_from_time ?? (toInputTime(r.effective_from as string) || '00:00:00'))
      const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
      const toT = String(r._effective_to_time ?? (toInputTime(r.effective_to as string) || '00:00:00'))
      const fromLocal = combineDateTimeLocal(fromD, fromT)
      const toLocal = toD ? combineDateTimeLocal(toD, toT) : null
      const caseType = determineConfigDateCase(fromLocal, toLocal)

      if (caseType === 'CASE_2' && !isModelRowActive(r, rowsRef.current)) {
        setDeleteTarget({
          id: mapId,
          label,
          message: 'Are you sure you want to delete this model configuration?',
        })
        return
      }

      setDeletePrecheckMapId(mapId)
      try {
        const { allowed, message } = await fetchCanDeleteModelMapping(mapId)
        if (!allowed) {
          showError(message ?? CANNOT_DELETE_MODEL_MAPPING_MESSAGE)
          return
        }
        setDeleteTarget({
          id: mapId,
          label,
          message: message?.trim() || 'Are you sure you want to delete this model configuration?',
        })
      } catch (err) {
        showApiError(err)
      } finally {
        setDeletePrecheckMapId(null)
      }
    }

    const handleDelete = async () => {
      if (!deleteTarget) return
      setSavingId(String(deleteTarget.id))
      try {
        await deletePropertyModelRow(deleteTarget.id)
        showSuccess('Model assignment removed.')
        await onRefresh()
      } catch (err) {
        showApiError(err)
      } finally {
        setSavingId(null)
        setDeleteTarget(null)
      }
    }

    const saveModelsIfDirtyFromParent = useCallback(
      async (_opts?: { silentSuccess?: boolean }): Promise<boolean> => {
        if (!allowModelConfig || !canMutate) return true
        const prop = propertyRef.current
        const snapshot = rowsRef.current
        if (!rowsDifferFromPropertySnapshot(snapshot, prop)) return true
        const keys = snapshot.map((row, i) => rowKey(row, i))
        setSavingId('__bulk__')
        setForceShowErrors(true)
        try {
          for (const key of keys) {
            const row = snapshot.find((x, i) => rowKey(x, i) === key)
            if (!row || isBaseModelRow(row)) continue
            const idx = snapshot.findIndex((x, i) => rowKey(x, i) === key)
            const fieldErrs = computeModelRowFieldErrors(row, idx >= 0 ? idx : 0, snapshot)
            if (fieldErrs.model || fieldErrs.effectiveFrom || fieldErrs.effectiveTo || fieldErrs.overlap) {
              return false
            }
            const ok = await upsertRowByKey(key, snapshot)
            if (!ok) return false
          }
          setEditingRowKey(null)
          setEditModeByKey({})
          // Persistence is via selected_models on the parent property POST.
          return true
        } catch (err) {
          showApiError(err)
          return false
        } finally {
          setSavingId(null)
        }
      },
      [allowModelConfig, canMutate, upsertRowByKey]
    )

    const validateModelRowsForSubmit = useCallback((): boolean => {
      if (!allowModelConfig) return true
      const snapshot = rowsRef.current
      setForceShowErrors(true)
      return !snapshot.some((row, index) => {
        if (isBaseModelRow(row)) return false
        const errs = computeModelRowFieldErrors(row, index, snapshot)
        return Boolean(errs.model || errs.effectiveFrom || errs.effectiveTo || errs.overlap)
      })
    }, [allowModelConfig])

    useImperativeHandle(
      ref,
      () => ({
        getModelConfigDirty: () =>
          allowModelConfig &&
          rowsDifferFromPropertySnapshot(rowsRef.current, propertyRef.current) &&
          canMutate,
        saveModelsIfDirtyFromParent,
        validateModelRowsForSubmit,
        getSelectedModelsJson: () =>
          allowModelConfig ? buildSelectedModelsFromRows(rowsRef.current) : null,
      }),
      [
        allowModelConfig,
        canMutate,
        saveModelsIfDirtyFromParent,
        validateModelRowsForSubmit,
        buildSelectedModelsFromRows,
      ]
    )

    // Angular: model products-config row only when allow_model_config
    if (!allowModelConfig) return null

    return (
      <div
        className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
        data-testid="property-model-config-tab"
      >
        <SectionCard
          icon={Layers}
          title="Model Information"
          testId="property-model-config-section"
          titleTestId="property-model-config-section-title"
          infoContent={<span>All time in ET</span>}
          infoTestId="property-model-config-et-info"
          headerActions={
            canMutate ? (
              <button
                type="button"
                onClick={handleAddRow}
                disabled={loading || savingId != null}
                data-testid="property-model-config-add-button"
                className={cn(companyFormStyles.btnPrimary, 'inline-flex items-center gap-1.5')}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
                Add New Model
              </button>
            ) : null
          }
        >
        <div className={configTableStyles.wrap}>
          <table className={cn(configTableStyles.table, 'text-xs sm:text-sm')} data-testid="property-model-config-table">
            <thead className={configTableStyles.thead}>
              <tr className={configTableStyles.theadRow}>
                <th className={cn(configTableStyles.th, configTableStyles.thProduct)}>Model <span className="text-red-500">*</span></th>
                <th className={cn(configTableStyles.th, configTableStyles.thFrom)}>Effective From</th>
                <th className={cn(configTableStyles.th, configTableStyles.thTo)}>Effective To</th>
                <th className={cn(configTableStyles.th, configTableStyles.thAction)}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className={configTableStyles.tr}><td colSpan={4} className={cn(configTableStyles.td, 'px-2 py-8 text-center text-slate-500')} data-label="">
                  <Loader2 className="h-6 w-6 animate-spin inline mr-2 align-middle" />Loading…
                </td></tr>
              )}
              {!loading && rows.map((r, i) => {
                const key = rowKey(r, i)
                const base = isBaseModelRow(r)
                const isNewDraft = isNewDraftRow(r)
                const mapDelId = propertyModelMapIdForDelete(r)
                const isUnlockedToOnly = Boolean(effectiveToUnlockKeys[key])
                const fromDate = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
                const fromTime = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
                const toDate = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
                const toTime = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
                const editMode = editModeByKey[key]
                const rowFullyEditable =
                  canEdit && (isNewDraft || (editingRowKey === key && editMode !== 'to-only'))
                const selectLocked = isConfigSelectLockedForActiveCase1(
                  fromDate ? combineDateTimeLocal(fromDate, fromTime || '00:00:00') : null,
                  toDate ? combineDateTimeLocal(toDate, toTime || '00:00:00') : null,
                  mapDelId != null,
                )
                const productSelectEnabled = rowFullyEditable && !selectLocked
                const fromFieldsEnabled = rowFullyEditable
                const toFieldsEnabled =
                  rowFullyEditable ||
                  (canEdit && isUnlockedToOnly) ||
                  (canEdit && editingRowKey === key && editMode === 'to-only')
                const rowActionBusy =
                  savingId === '__bulk__' ||
                  savingId === key ||
                  (mapDelId != null && savingId != null && String(savingId) === String(mapDelId))
                const deleteButtonDisabled =
                  !canDelete ||
                  rowActionBusy ||
                  (deletePrecheckMapId != null && deletePrecheckMapId === mapDelId)

                const tomorrowDateObj = getTomorrowEasternDate()
                // Angular getDateFilterEffectiveFrom / getDateFilterEffectiveTo.
                const filterFromDateFn = (date: Date) =>
                  isEffectiveFromCalendarDayAllowed(date) &&
                  !isDayBlockedByOtherModels(date, key, rows)
                const fromDateObj = fromDate ? parseDateStrToDate(fromDate) : null
                const filterToDateFn = (date: Date) => {
                  if (!fromDateObj) return false
                  if (normalizeDateObj(date).getTime() < normalizeDateObj(fromDateObj).getTime()) return false
                  return !isDayBlockedByOtherModels(date, key, rows)
                }
                const effectiveToMinDate = fromDateObj ?? tomorrowDateObj
                const fieldErrors = forceShowErrors ? computeModelRowFieldErrors(r, i, rows) : {}
                const showActive = isModelRowActive(r, rows)
                const baseLabel = getBaseModelName(catalog)

                return (
                  <tr key={`${key}-${i}`} className={configTableStyles.tr}>
                    <td className={cn(configTableStyles.td, CONFIG_CELL_PAD)} data-label="Model">
                      {base ? (
                        <div>
                          <span className="box-border flex h-[42px] w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm leading-5 text-slate-900">
                            {baseLabel}
                          </span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            <span className="rounded bg-[rgb(21_151_255)] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              Default
                            </span>
                            {showActive ? (
                              <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                Active
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="relative w-full">
                            <select
                              className={cn(
                                'box-border h-[42px] w-full appearance-none rounded-md border py-0 pl-3 pr-9 text-sm leading-5 focus:outline-none bg-white',
                                fieldErrors.model
                                  ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                  : 'border-slate-200 focus:border-[rgb(21_151_255)]',
                              )}
                              disabled={!productSelectEnabled || savingId === key || savingId === '__bulk__'}
                              data-testid={`property-model-config-row-${key}-model-select`}
                              value={String(r.model_id ?? '')}
                              onChange={(e) => {
                                const v = e.target.value
                                const opt = catalog.find((c) => catalogOptionValue(c) === v)
                                updateRow(key, { model_id: v, model_name: opt ? catalogOptionLabel(opt) : '' })
                              }}
                            >
                              <option value="">Select Model</option>
                              {catalog
                                .filter((c) => Number(catalogOptionValue(c)) !== 1)
                                .map((c, j) => (
                                <option key={j} value={catalogOptionValue(c)}>{catalogOptionLabel(c)}</option>
                              ))}
                              {(() => {
                                const v = String(r.model_id ?? '')
                                const lab = displayProductName(r)
                                if (!v || catalog.some((c) => catalogOptionValue(c) === v)) return null
                                return <option value={v}>{lab || `Model ${v}`}</option>
                              })()}
                            </select>
                            <ChevronDown
                              className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                              aria-hidden
                            />
                          </div>
                          <ConfigFieldError
                            message={fieldErrors.model}
                            testId={`property-model-config-row-${key}-model-error`}
                          />
                          {showActive && Number(r.model_id) ? (
                            <span className="mt-1 inline-block rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              Active
                            </span>
                          ) : null}
                        </div>
                      )}
                    </td>

                    <td className={cn(configTableStyles.td, CONFIG_CELL_PAD)} data-label="Effective From">
                      <div>
                        {base && !isNewDraft ? (
                          <ReadOnlyEffectiveFields
                            dateLabel={toDisplayDate(r.effective_from as string)}
                            timeLabel={toDisplayTime(r.effective_from as string)}
                            dateTimeRowClass={configTableStyles.dateTimeRow}
                            dateFieldClass={configTableStyles.dateField}
                            timeFieldClass={configTableStyles.timeField}
                          />
                        ) : (
                          <div className={configTableStyles.dateTimeRow}>
                            <div className={configTableStyles.dateField}>
                              <DatePicker
                                value={fromDate}
                                onChange={(v) =>
                                  updateRow(key, {
                                    _effective_from_date: v,
                                    // Angular matDatepicker yields a midnight Date → time shows 00:00:00.
                                    ...(v && !fromTime ? { _effective_from_time: '00:00:00' } : {}),
                                  })
                                }
                                disabled={!fromFieldsEnabled || savingId === key || savingId === '__bulk__'}
                                allowFutureDates={true}
                                minDate={tomorrowDateObj}
                                filterDate={filterFromDateFn}
                                showHelperText={false}
                                placeholder="MM/DD/YYYY"
                                className="!box-border !h-[42px] w-full text-sm"
                                popperPlacement="top-start"
                                popperFixed
                                error={fieldErrors.effectiveFrom}
                                dataTestId={`property-model-config-row-${key}-effective-from-date`}
                              />
                            </div>
                            <TimeFieldInput
                              className={configTableStyles.timeField}
                              disabled={!fromFieldsEnabled || savingId === key || savingId === '__bulk__'}
                              value={fromTime}
                              onChange={(v) => updateRow(key, { _effective_from_time: v })}
                              dataTestId={`property-model-config-row-${key}-effective-from-time`}
                            />
                          </div>
                        )}
                        <ConfigFieldError
                          message={fieldErrors.overlap}
                          testId={`property-model-config-row-${key}-overlap-error`}
                        />
                      </div>
                    </td>

                    <td className={cn(configTableStyles.td, CONFIG_CELL_PAD)} data-label="Effective To">
                      {base && !isNewDraft ? (
                        <ReadOnlyEffectiveFields
                          muted
                          dateLabel={toDisplayDate(r.effective_to as string)}
                          timeLabel={toDisplayTime(r.effective_to as string)}
                          dateTimeRowClass={configTableStyles.dateTimeRow}
                          dateFieldClass={configTableStyles.dateField}
                          timeFieldClass={configTableStyles.timeField}
                        />
                      ) : (
                        <div className={configTableStyles.dateTimeRow}>
                          <div className={configTableStyles.dateField}>
                            <DatePicker
                              value={toDate}
                              onChange={(v) =>
                                updateRow(key, {
                                  _effective_to_date: v,
                                  ...(v && !toTime ? { _effective_to_time: '00:00:00' } : {}),
                                })
                              }
                              disabled={!toFieldsEnabled || savingId === key || savingId === '__bulk__'}
                              allowFutureDates={true}
                              minDate={effectiveToMinDate}
                              filterDate={filterToDateFn}
                              showHelperText={false}
                              placeholder="MM/DD/YYYY"
                              className="!box-border !h-[42px] w-full text-sm"
                              popperPlacement="top-start"
                              popperFixed
                              error={fieldErrors.effectiveTo}
                              dataTestId={`property-model-config-row-${key}-effective-to-date`}
                            />
                          </div>
                          <TimeFieldInput
                            className={configTableStyles.timeField}
                            disabled={!toFieldsEnabled || savingId === key || savingId === '__bulk__'}
                            value={toTime}
                            onChange={(v) => updateRow(key, { _effective_to_time: v })}
                            dataTestId={`property-model-config-row-${key}-effective-to-time`}
                          />
                        </div>
                      )}
                    </td>

                    <td className={cn(configTableStyles.td, CONFIG_CELL_PAD)} data-label="Action">
                      {!base && (canEdit || canDelete || isNewDraft) ? (
                        <div className="flex h-[42px] items-center gap-1">
                          {/* Angular shouldShowCancelButton: editing OR unlocked blank To — never for new drafts */}
                          {!isNewDraft &&
                          (editingRowKey === key || (isUnlockedToOnly && !rowFullyEditable)) &&
                          canEdit ? (
                            <button
                              type="button"
                              title="Cancel"
                              aria-label="Cancel edit"
                              disabled={savingId != null}
                              data-testid={`property-model-config-row-${key}-cancel-button`}
                              className="rounded border border-amber-200 bg-amber-50 p-1.5 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              onClick={() => {
                                revertRowToServerSnapshot(key)
                                setEditingRowKey(null)
                                setEditModeByKey((prev) => {
                                  const next = { ...prev }
                                  delete next[key]
                                  return next
                                })
                                setEffectiveToUnlockKeys((prev) => {
                                  const next = { ...prev }
                                  delete next[key]
                                  return next
                                })
                              }}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          ) : null}
                          {/* Angular shouldShowEditButton: saved only; hide while editing / unlocked To */}
                          {!isNewDraft &&
                          canEdit &&
                          editingRowKey !== key &&
                          !isUnlockedToOnly ? (
                            <button
                              type="button"
                              title="Edit"
                              aria-label="Edit row"
                              disabled={!canEdit || savingId != null}
                              data-testid={`property-model-config-row-${key}-save-button`}
                              className="rounded border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-[rgb(21_151_255)] disabled:opacity-50"
                              onClick={() => void handlePencilClick(key)}
                            >
                              {savingId === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                            </button>
                          ) : null}
                          {(isNewDraft || canDelete) ? (
                          <button
                            type="button"
                            title={isNewDraft ? 'Remove draft row' : 'Delete'}
                            disabled={isNewDraft ? savingId != null : deleteButtonDisabled}
                            data-testid={`property-model-config-row-${key}-delete-button`}
                            className="rounded border border-slate-200 p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            onClick={() => {
                              if (isNewDraft) {
                                setRows((prev) => prev.filter((x) => String(rowId(x) ?? '') !== String(rowId(r))))
                                if (editingRowKey === key) setEditingRowKey(null)
                              } else {
                                void requestDeleteRow(r, displayProductName(r) || 'this row')
                              }
                            }}
                          >
                            {deletePrecheckMapId != null && deletePrecheckMapId === mapDelId ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <Trash2 className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </SectionCard>

        <ConfirmDialog
          isOpen={deleteTarget != null}
          title="Delete Model"
          message={
            deleteTarget?.message ||
            (deleteTarget ? `Remove "${deleteTarget.label}" from this property?` : '')
          }
          primaryLabel="Delete"
          onPrimary={() => void handleDelete()}
          secondaryLabel="Cancel"
          onSecondary={() => setDeleteTarget(null)}
          {...companyEditConfirmPresentation}
          overlayDataTestId="property-model-config-delete-confirm-overlay"
          primaryButtonDataTestId="property-model-config-delete-confirm-remove-button"
          secondaryButtonDataTestId="property-model-config-delete-confirm-cancel-button"
          closeButtonDataTestId="property-model-config-delete-confirm-close-button"
        />
      </div>
    )
  }
)

PropertyModelConfigTab.displayName = 'PropertyModelConfigTab'
